/* eslint-disable @typescript-eslint/use-unknown-in-catch-callback-variable */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { ActionReference, DefaultGameState, GameObjectReference, GameState, ItemReference } from "@shared/types";
import { GameRouteService } from "../../services/GameRouteService";
import styles from "../../styles";

// Import components
import "../inventory/InventoryComponent";
import "../common/ActionButtonsComponent";
import "../map/MapComponent";
import "../popups/PopupComponent";
import "../popups/RestartConfirmationComponent";
import "../popups/EscapeRestartComponent";
import "../common/TravelButtonsComponent";
import "../risk/RiskBarComponent";
import "../audio/AudioComponent";
import { ActionButtonsComponent } from "../common/ActionButtonsComponent";
import { AchievementGridComponent } from "../achievement/AchievementGridComponent";
import { AchievementItemComponent } from "../achievement/AchievementComponent";
import "../minigame/KeyTheftComponent";
import { Timer } from "./TimeClass";
import { AskNameComponent } from "../achievement/AskNameComponent";
import { CaughtRestartComponent } from "../popups/CaughtRestartComponent";

// Define interfaces for custom elements
interface InventoryElement extends HTMLElement {
    items: ItemReference[];
    selectedItems: Set<ItemReference>;
    onItemClick: (item: ItemReference) => void;
}

interface RoomsElement extends HTMLElement {
    objects: ItemReference[];
    selectedObjects: Set<ItemReference>;
    onObjectClick: (object: ItemReference) => void;
}

interface MapElement extends HTMLElement {
    rooms: ItemReference[];
    currentRoomAlias: string;
    onRoomChange: (roomAlias: string) => void;
}

interface PopupElement extends HTMLElement {
    content: string;
    onClose: () => void;
}

interface AchievementPopupElement extends HTMLElement {
    content: string;
}

interface RestartConfirmationElement extends HTMLElement {
    onConfirm: () => void;
    onCancel: () => void;
}

interface EscapeRestartComponent extends HTMLElement {
    onConfirmEscape: () => void;
    onCancelEscape: () => void;
}

// Add a new interface for TravelButtonsElement
interface TravelButtonsElement extends HTMLElement {
    actions: ActionReference[];
    selectedAction: ActionReference | undefined;
    onActionClick: (action: ActionReference) => void;
}

// Add a new interface for RiskBarElement
interface RiskBarElement extends HTMLElement {
    items: ItemReference[];
    config: {
        defaultDuration: number;
        riskMultiplier: number;
    };
}

interface KeyTheftGameElement extends HTMLElement {
    // Properties from the component
    keysStolen?: number;
    guardPosition?: number;
    playerPosition?: number;
    isStealingEnabled?: boolean;

    // Methods for game interaction
    movePlayer?(direction: "left" | "right"): void;
    stealKey?(): Promise<void>;
    resetGame?(): Promise<void>;

    // Event methods you might want to add if needed
    onGameComplete?: (success: boolean) => void;
}

const timer: Timer = new Timer();

/**
 * Main canvas component for the game
 */
export class CanvasComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _currentGameState?: DefaultGameState;
    private _selectedActionButton?: ActionReference;
    private _selectedGameObjectButtons: Set<ItemReference> = new Set<ItemReference>();
    private visited: boolean = false;

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.refreshGameState().then(() => {}).catch(console.error);
    }

    /**
     * Refresh the game state from the server
     */
    private async refreshGameState(): Promise<void> {
        try {
            const state: GameState | undefined = await this._gameRouteService.getGameState();
            this.updateGameState(state);
        }
        catch (error) {
            console.error("Failed to get game state:", error);

            // Redirect to not found page if there's an error
            this.showPopup("<p>There was an error loading the game state. Please try refreshing the page.</p>");
        }
    }

    /**
     * Update the game state
     */
    private updateGameState(state: GameState): void {
        this._currentGameState = state as DefaultGameState;

        // Clear selections when state changes
        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();
        this.render();
        void this.checkAchievements();
    }

    /**
     * Check and show achievement notification if any object has been achieved
     */
    private async checkAchievements(): Promise<void> {
        if (this._currentGameState?.achievements) {
            const state: GameState = await this._gameRouteService.getGameState() as DefaultGameState;
            for (const achievement of state.achievements) {
                if (achievement.achieved && !achievement.visited && achievement.layer !== 0) {
                    void this._gameRouteService.changeDisplay(achievement.alias);
                    const time: [number, number] = timer.getElapsedTime();
                    /** Always two values */
                    const playerName: string | null = localStorage.getItem("playerName");
                    if (playerName) {
                        void this._gameRouteService.updateAchievement(achievement.alias, playerName, time[0], time[1]);
                    }
                    else {
                        console.error("Name not found!");
                    }
                    this.showAchievementPopup(achievement);
                    const item: AchievementItemComponent = new AchievementItemComponent(achievement);
                    void item.createElement();
                }
            }
        }
    }

    /**
     * Render the component
     */
    private render(): void {
        if (!this._currentGameState) {
            return;
        }

        // Create shadow DOM if it doesn't exist
        if (!this.shadowRoot) {
            this.attachShadow({ mode: "open" });
        }

        // Render the component
        if (this.shadowRoot) {
            const content: string = `
                <style>${styles.canvas}</style>
                <div class="title">${this.renderTitle()}</div>
                <div class="left-column-container">
                    <div class="chat-box">
                        <div class="room-name">${this._currentGameState.roomName || ""}</div>
                        ${this.renderChatBox()}
                    </div>
                    <game-travel-buttons id="travel-buttons"></game-travel-buttons>
                </div>
                <div class="main-content">
                    ${this.renderRoomImage()}
                    ${this.renderContent()}
                </div>
                <div class="footer">${this.renderFooter()}</div>
            `;
            this.shadowRoot.innerHTML = content;
            this.setupAudioButtonListener();
            this.styleAudioButton();

            // Setup event listeners
            this.setupInventoryListeners();
            this.setupAchievementListeners();
            this.setupRoomsListeners();
            this.setupActionButtonsListeners();
            this.setupTravelButtonsListeners();
            this.setupMapButtonListener();
            this.setupRestartButtonListener();
            this.setupKeyTheftButtonListener();
            this.setupCaughtRestartListener();
            const alias: string = this._currentGameState.roomAlias;
            if (alias === "escape1" || alias === "escape2" || alias === "escape3") {
                this.escapeRestartGame();
            }
            else if (alias === "caught") {
                this.caughtRestartGame();
            }

            if (!this.visited) {
                this.showPlayerNamePopup();
                this.visited = true;
            }
        }
    }

    /**
     * Render the title
     */
    private renderTitle(): string {
        return this._currentGameState?.roomName || "Prison Break";
    }

    /**
     * Render the chat box with game text
     */
    private renderChatBox(): string {
        if (!this._currentGameState) {
            return "";
        }

        const messages: string[] = Array.isArray(this._currentGameState.text)
            ? this._currentGameState.text
            : this._currentGameState.text ? [this._currentGameState.text] : [];

        return messages.map(message => `
                <p>${message}</p>
        `).join("");
    }

    /**
     * Render the room image if available
     */
    private renderRoomImage(): string {
        if (!this._currentGameState || !this._currentGameState.roomImages || this._currentGameState.roomImages.length === 0) {
            return "";
        }

        // Create image layers
        const imageLayers: string = this._currentGameState.roomImages.map(image => {
            const imageUrl: string = `/assets/img/rooms/${image}.png`;
            return `<img src="${imageUrl}" alt="${this._currentGameState?.roomName || ""}" class="room-image-layer">`;
        }).join("");

        return `
            <div class="room-image-container">
                ${imageLayers}
            </div>
        `;
    }

    /**
     * Render the content
     */
    private renderContent(): string {
        return `
            <game-risk-bar id="risk-bar"></game-risk-bar>
            <game-inventory id="inventory"></game-inventory>
        `;
    }

    /**
     * Render the footer
     */
    private renderFooter(): string {
        return `
            <game-action-buttons id="action-buttons"></game-action-buttons>
            <div class="game-controls">
                <div>
                    <button class="map-button" id="map-button">View Prison Map</button>
                    <button class="achievement-button" id="achievement-button">View Achievements</button>
                </div>
                <button class="restart-button" id="restart-button">Restart Game</button>
            </div>
        `;
    }

    /**
     * Setup inventory listeners
     */
    private setupInventoryListeners(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        const inventoryComponent: InventoryElement | null = this.shadowRoot.getElementById("inventory") as InventoryElement;
        if (inventoryComponent && "items" in inventoryComponent) {
            // Set inventory items
            inventoryComponent.items = this._currentGameState.inventory || [];

            // Set selected items
            inventoryComponent.selectedItems = this._selectedGameObjectButtons;

            // Set click handler
            inventoryComponent.onItemClick = (item: ItemReference) => {
                this.handleClickGameObject(item).catch(console.error);
            };
        }

        // Setup the risk bar with the same inventory items
        this.setupRiskBarListener();
    }

    private setupAudioButtonListener(): void {
        if (!this.shadowRoot) {
            return;
        }

        const audioButton: HTMLElement | null = this.shadowRoot.getElementById("audio-button");
        if (audioButton) {
            audioButton.addEventListener("click", () => {
                this.showAudioPopup();
            });
        }
    }

    private styleAudioButton(): void {
        if (!this.shadowRoot) {
            return;
        }

        const audioButton: HTMLElement | null = this.shadowRoot.getElementById("audio-button");
        if (audioButton) {
            audioButton.style.position = "absolute";
            audioButton.style.top = "10px";
            audioButton.style.right = "10px";
            audioButton.style.zIndex = "1000";
        }
    }

    private showAudioPopup(): void {
        if (!this.shadowRoot) {
            return;
        }

        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;
        popupElement.content = "<p>Adjust your audio settings here.</p>";
        popupElement.onClose = () => this.render();
        this.shadowRoot.appendChild(popupElement);
    }

    /**
     * Setup the event listener for the achievement button
    */
    private setupAchievementListeners(): void {
        this.shadowRoot?.querySelectorAll(".achievement-button").forEach(item => {
            item.addEventListener("click", () => {
                if (this._currentGameState) {
                    this.showAchievements(this._currentGameState);
                }
            });
        });
    }

    /**
     * Setup rooms listeners
     */
    private setupRoomsListeners(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        const roomsComponent: RoomsElement | null = this.shadowRoot.getElementById("rooms") as RoomsElement;
        if (roomsComponent && "objects" in roomsComponent) {
            // Set room objects
            roomsComponent.objects = this._currentGameState.objects || [];

            // Set selected objects
            roomsComponent.selectedObjects = this._selectedGameObjectButtons;

            // Set click handler
            roomsComponent.onObjectClick = (object: ItemReference) => {
                this.handleClickGameObject(object).catch(console.error);
            };
        }
    }

    /**
     * Setup action buttons listeners
     */
    private setupActionButtonsListeners(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        const actionButtonsComponent: ActionButtonsComponent | null = this.shadowRoot.getElementById("action-buttons") as ActionButtonsComponent;
        if (actionButtonsComponent) {
            // Set available actions
            actionButtonsComponent.actions = this._currentGameState.actions;

            // Set selected action
            actionButtonsComponent.selectedAction = this._selectedActionButton || null;

            // Set click handler
            actionButtonsComponent.onActionClick = (action: ActionReference) => {
                this.handleClickAction(action).catch(console.error);
            };
        }
    }

    /**
     * Setup travel buttons listeners
     */
    private setupTravelButtonsListeners(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        const travelButtonsComponent: TravelButtonsElement | null = this.shadowRoot.getElementById("travel-buttons") as TravelButtonsElement;
        if (travelButtonsComponent && "actions" in travelButtonsComponent) {
            // Filter only travel actions
            const travelActions: ActionReference[] = (this._currentGameState.actions || [])
                .filter(action => action.isTravelAction);

            // Set travel actions
            travelButtonsComponent.actions = travelActions;

            // Set selected action
            travelButtonsComponent.selectedAction =
                this._selectedActionButton && this._selectedActionButton.isTravelAction
                    ? this._selectedActionButton
                    : undefined;

            // Set click handler
            travelButtonsComponent.onActionClick = (action: ActionReference) => {
                this.handleClickAction(action).catch(console.error);
            };
        }
    }

    /**
     * Setup map button listener
     */
    private setupMapButtonListener(): void {
        if (!this.shadowRoot) {
            return;
        }

        const mapButton: HTMLElement | null = this.shadowRoot.getElementById("map-button");
        if (mapButton) {
            mapButton.addEventListener("click", () => {
                this.showPrisonMapPopup();
            });
        }
    }

    /**
     * Setup restart button listener
     */
    private setupRestartButtonListener(): void {
        if (!this.shadowRoot) {
            return;
        }

        const restartButton: HTMLElement | null = this.shadowRoot.getElementById("restart-button");
        if (restartButton) {
            restartButton.addEventListener("click", () => {
                this.handleRestartGame();
            });
        }
    }

    /**
     * Setup risk bar listener
     */
    private setupRiskBarListener(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        const riskBarComponent: RiskBarElement | null = this.shadowRoot.getElementById("risk-bar") as RiskBarElement;
        if (riskBarComponent && "items" in riskBarComponent) {
            // Set inventory items for risk calculation
            riskBarComponent.items = this._currentGameState.inventory || [];
            // Configure risk bar (optional customization)
            riskBarComponent.config = {
                defaultDuration: 300, // 5 minutes in seconds
                riskMultiplier: 0.5,
            };
        }
    }

    /**
     * Handle action button click
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        if (!this._currentGameState) {
            return;
        }

        // Toggle selection if it's the same button
        if (this._selectedActionButton === button) {
            this._selectedActionButton = undefined;
        }
        else {
            this._selectedActionButton = button;
        }

        // Special handling for key theft minigame
        if (button.alias === "stealkeys") {
            this.showKeyTheftMinigamePopup();
            return;
        }

        // If the action needs an object, just update the UI and wait for object selection
        if (button.needsObject) {
            this.render();
            return;
        }

        // For actions that don't need objects, execute immediately
        try {
            const state: GameState | undefined = await this._gameRouteService.executeAction(
                button.alias
            );

            if (state) {
                this.updateGameState(state);
            }
        }
        catch (error) {
            console.error("Failed to perform action:", error);
        }
    }

    /**
     * Handle game object click
     */
    private async handleClickGameObject(button: ItemReference): Promise<void> {
        if (!this._currentGameState) {
            return;
        }

        // Toggle selection
        if (this._selectedGameObjectButtons.has(button)) {
            this._selectedGameObjectButtons.delete(button);
        }
        else {
            this._selectedGameObjectButtons.add(button);
        }

        // If we have both an action and at least one game object selected, perform the action
        if (this._selectedActionButton && this._selectedGameObjectButtons.size > 0) {
            try {
                const gameObjects: ItemReference[] = Array.from(this._selectedGameObjectButtons);
                const state: GameState | undefined = await this._gameRouteService.executeAction(
                    this._selectedActionButton.alias,
                    gameObjects.map(object => object.alias)
                );

                if (state) {
                    this.updateGameState(state);
                }
            }
            catch (error) {
                console.error("Failed to perform action:", error);
            }
        }
        else {
            // If we have a single object selected and no action, show the object description
            if (this._selectedGameObjectButtons.size === 1 && !this._selectedActionButton) {
                const gameObject: ItemReference = Array.from(this._selectedGameObjectButtons)[0];
                this.showPopup(gameObject.name || "No description available.", gameObject);
            }
            else {
                this.render();
            }
        }
    }

    /**
     * Show a popup with content
     */
    private showPopup(content: string, item?: ItemReference): void {
        if (!this.shadowRoot) {
            return;
        }

        // Create popup component
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        // Set content and close handler
        popupElement.content = content;
        popupElement.onClose = () => {
            // Deselect the item when closing the popup
            if (item) {
                this._selectedGameObjectButtons.delete(item);
            }
        };

        // Add to shadow root
        this.shadowRoot.appendChild(popupElement);
    }

    /**
     * Show player name input pop-up if in startup room
     */
    private showPlayerNamePopup(): void {
        if (!this.shadowRoot || this._currentGameState?.roomAlias !== "startup") {
            return;
        }
        localStorage.removeItem("playerName");
        const popupElement: AskNameComponent = new AskNameComponent();
        this.shadowRoot.appendChild(popupElement);
    }

    /**
     * Shows the achievements popup
     */
    private showAchievements(_currentGameState: DefaultGameState): void {
        if (!this.shadowRoot || !_currentGameState) {
            return;
        }

        // Remove any existing popup before adding a new one
        const existingPopup: Element | null = this.shadowRoot.querySelector("game-popup");
        if (existingPopup) {
            existingPopup.remove();
        }

        // Create new popup container
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        // Set close behavior
        popupElement.onClose = () => {
            popupElement.remove();
        };

        // Append the popup to shadowRoot first (if it's not already present)
        this.shadowRoot.appendChild(popupElement);

        // Ensure we have the correct target for content
        const popupContentElement: HTMLElement | null = popupElement.querySelector(".popup-content");
        if (!popupContentElement) return;

        // Ensure the content area is empty before adding new elements
        popupContentElement.innerHTML = "";

        // Create achievements grid and append to content
        const grid: AchievementGridComponent = new AchievementGridComponent(_currentGameState.achievements);

        // Use a fresh container for grid to prevent hierarchy issues
        const gridContainer: HTMLDivElement = document.createElement("div");
        gridContainer.className = "achievement-grid-container";
        grid.createElement(gridContainer);

        popupContentElement.appendChild(gridContainer);
    }

    /**
     * Show a popup when an achievement is unlocked
     */
    private showAchievementPopup(achievement: GameObjectReference): void {
        if (!this.shadowRoot) {
            return;
        }

        const content: string = `
            <div class="achievement-popup">
                <img src="${achievement.image ?? "../assets/img/items/lighter.png"}" alt="Achievement Icon" width="32" height="32"/>
                <p>${achievement.alias} has been achieved!</p>
            </div>
        `;

        const popupElement: AchievementPopupElement = document.createElement("game-popup") as AchievementPopupElement;
        popupElement.content = content;

        this.shadowRoot.appendChild(popupElement);

        const popup: HTMLElement = this.shadowRoot.querySelector(".achievement-popup")!;
        if (popup) {
            popup.classList.add("achievement-small-popup");
        }
        this.shadowRoot.appendChild(popup);
        popupElement.remove();

        // Automatically close the pop-up after 5 seconds
        setTimeout(() => {
            popup.remove();
        }, 5000);
    }

    /**
     * Show the prison map popup
     */
    private showPrisonMapPopup(): void {
        if (!this.shadowRoot || !this._currentGameState) {
            return;
        }

        // Create popup content container
        const popupContent: HTMLElement = document.createElement("div");
        popupContent.className = "prison-map-popup";

        // Create popup component
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        // Add popup to shadow root first
        this.shadowRoot.appendChild(popupElement);

        // Set close handler
        popupElement.onClose = () => {
            popupElement.remove();
        };

        // Get the popup content element
        const popupContentElement: HTMLElement | null = popupElement.querySelector(".popup-content");
        if (!popupContentElement) return;

        // Create map component
        const mapComponent: MapElement = document.createElement("game-map") as MapElement;

        // Set rooms and current room
        mapComponent.rooms = this._currentGameState.rooms.map(room => ({
            alias: room.alias,
            name: room.name,
            image: undefined,
            riskFactor: 0,
        }));
        mapComponent.currentRoomAlias = this._currentGameState.roomAlias || "";

        // Add room change handler to close popup and update UI
        mapComponent.onRoomChange = async (roomAlias: string) => {
            try {
                // Request room change
                await this._gameRouteService.requestRoomChange(roomAlias);

                // Close the popup
                popupElement.remove();

                // Refresh game state to update UI
                await this.refreshGameState();
            }
            catch (error) {
                console.error("Error changing rooms:", error);
            }
        };

        // Append map component directly to the popup content
        popupContentElement.appendChild(mapComponent);
    }

    private escapeRestartGame(): EscapeRestartComponent | undefined {
        if (!this.shadowRoot) {
            return undefined;
        }

        // Remove any existing popups first
        this.shadowRoot.querySelectorAll(".popup-overlay").forEach(popup => popup.remove());

        // Create restart confirmation component
        const restartConfirmationElement: EscapeRestartComponent = document.createElement("escape-restart-confirmation") as EscapeRestartComponent;

        // Set handlers
        restartConfirmationElement.onConfirmEscape = () => {
            this.restartGame();
        };

        restartConfirmationElement.onCancelEscape = () => {
            void this._gameRouteService.requestRoomChange("cell");
            void this.refreshGameState();
            this.shadowRoot?.querySelectorAll(".popup-overlay").forEach(popup => popup.remove());
            this.render();
            return;
        };

        // Add to shadow root
        this.shadowRoot.appendChild(restartConfirmationElement);
        return restartConfirmationElement;
    }

    private caughtRestartGame(): CaughtRestartComponent | undefined {
        if (!this.shadowRoot) {
            return undefined;
        }

        // Remove any existing popups first
        this.shadowRoot.querySelectorAll(".popup-overlay").forEach(popup => popup.remove());

        // Create restart confirmation component
        const restartConfirmationElement: CaughtRestartComponent = document.createElement("caught-restart-confirmation") as CaughtRestartComponent;

        // Set handlers
        restartConfirmationElement.onConfirmEscape = () => {
            this.restartGame();
        };

        restartConfirmationElement.onCancelEscape = () => {
            // In caught state, we only allow restart
            this.restartGame();
        };

        this.shadowRoot.appendChild(restartConfirmationElement);
        return restartConfirmationElement;
    }

    /**
     * Handle restart game button click
     */
    private handleRestartGame(): void {
        if (!this.shadowRoot) {
            return;
        }

        // Remove any existing popups first
        this.shadowRoot.querySelectorAll(".popup-overlay").forEach(popup => popup.remove());

        // Create restart confirmation component
        const restartConfirmationElement: RestartConfirmationElement = document.createElement("game-restart-confirmation") as RestartConfirmationElement;

        // Set handlers
        restartConfirmationElement.onConfirm = () => {
            this.restartGame();
        };

        restartConfirmationElement.onCancel = () => {
            this.render();
        };

        // Add to shadow root
        this.shadowRoot.appendChild(restartConfirmationElement);
    }

    /**
     * Restart the game
     */
    private restartGame(): void {
        this._gameRouteService.restartGame()
            .then((state: GameState | undefined) => {
                if (state) {
                    this.updateGameState(state);
                }
            })
            .catch((error: unknown) => {
                console.error("Failed to restart game:", error);
            });
    }

    /**
     * Show the key theft minigame popup
    */
    private showKeyTheftMinigamePopup(): void {
        if (!this.shadowRoot) {
            return;
        }

        // Create popup component
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        // Set a custom class for styling the minigame popup
        popupElement.className = "minigame-popup";

        // Add popup to shadow root
        this.shadowRoot.appendChild(popupElement);

        // Set close handler
        popupElement.onClose = () => {
            this.render();
        };

        // Get the popup content element
        const popupContentElement: HTMLElement | null = popupElement.querySelector(".popup-content");
        if (!popupContentElement) return;

        // Add a title
        const titleElement: HTMLHeadingElement = document.createElement("h2");
        titleElement.textContent = "Key Theft Minigame";
        titleElement.className = "minigame-title";
        popupContentElement.appendChild(titleElement);

        // Create minigame component
        const keyTheftGame: KeyTheftGameElement = document.createElement("key-theft-game") as KeyTheftGameElement;

        // Add event listener for when keys are collected
        keyTheftGame.addEventListener("keysCollected", async () => {
        // Close the popup
            popupElement.remove();

            // Refresh game state to update inventory
            await this.refreshGameState();
        });

        // Append minigame component to the popup content
        popupContentElement.appendChild(keyTheftGame);
    }

    /**
     * Setup key theft minigame button listener
     */
    private setupKeyTheftButtonListener(): void {
        if (!this.shadowRoot) {
            return;
        }

        const keyTheftButton: HTMLElement | null = this.shadowRoot.getElementById("key-theft-button");
        if (keyTheftButton) {
            keyTheftButton.addEventListener("click", () => {
                this.showKeyTheftMinigamePopup();
            });
        }
    }

    /**
     * Setup caught restart listener if risk bar is reached limit
     */
    private setupCaughtRestartListener(): void {
        if (!this.shadowRoot) {
            return;
        }

        const riskBar: HTMLElement | null = this.shadowRoot.getElementById("risk-bar");
        if (riskBar) {
            riskBar.addEventListener("gameOver", async () => {
                await this._gameRouteService.executeAction("gameover");
                await this.refreshGameState();
            });
        }
    }
}
